import { inject, Injectable, signal } from "@angular/core";
import { AppSettings, defaults } from "../config";
import { BehaviorSubject, Observable } from "rxjs";
import { TranslateService } from "@ngx-translate/core";

@Injectable({
  providedIn: "root",
})
export class CoreService {
  get appName() {
    return "Treeslo";
  }

  translate = inject(TranslateService);
  private optionsSignal = signal<AppSettings>(this.loadStoredSettings());

  private loadStoredSettings(): AppSettings {
    const raw = localStorage.getItem(`${this.appName}_Settings`);
    if (!raw) {
      return { ...defaults };
    }

    const parsed = JSON.parse(raw) as Partial<AppSettings>;
    return {
      ...defaults,
      ...parsed,
      activeTheme: "green_theme",
      // App uses vertical shell; stale theme customizer flags hid the sidenav.
      horizontal: false,
      navPos: "side",
    };
  }
  private notify$ = new BehaviorSubject<Record<string, any>>({});

  constructor() {
    this.notify$.next(this.optionsSignal());

    const savedLang = this.getLanguage();
    this.translate.setDefaultLang(savedLang);
    this.translate.use(savedLang);
  }

  // Observable for notification updates
  get notify(): Observable<Record<string, any>> {
    return this.notify$.asObservable();
  }

  // Get the current options
  getOptions(): AppSettings {
    return this.optionsSignal();
  }

  setOptions(options: Partial<AppSettings>) {
    this.optionsSignal.update((current) => ({
      ...current,
      ...options,
    }));
    this.notify$.next(this.optionsSignal());
    localStorage.setItem(
      `${this.appName}_Settings`,
      JSON.stringify(this.optionsSignal()),
    );
  }

  setLanguage(lang: string) {
    this.setOptions({ language: lang });
  }

  getLanguage() {
    return this.getOptions().language;
  }

  /** Sync `html` theme classes with stored settings (required on routes without Full/Blank layout). */
  applyThemeToDocument(options: AppSettings = this.getOptions()): void {
    if (typeof document === "undefined") {
      return;
    }

    const root = document.documentElement;
    const shouldBeDark = options.theme === "dark";

    root.classList.toggle("dark-theme", shouldBeDark);
    root.classList.toggle("light-theme", !shouldBeDark);

    const activeTheme = options.activeTheme;
    if (!activeTheme) {
      return;
    }

    root.classList.forEach((className) => {
      if (className.endsWith("_theme") && className !== activeTheme) {
        root.classList.remove(className);
      }
    });

    if (!root.classList.contains(activeTheme)) {
      root.classList.add(activeTheme);
    }
  }
}
